// src/services/contactsService.js
// CRUD contacts + helpers de búsqueda y linkado con events (event_contacts).
// Objetivo: centralizar todas las queries de contactos y evitar lógica duplicada en UI.
//
// Nota: no impongo tu esquema exacto. Uso nombres de campos comunes:
// - contacts: id, team_id, type, name, email, phone, notes
// - event_contacts: event_id, contact_id, is_primary, role_in_event, notes
// Si algún campo difiere, lo ajustamos cuando lleguemos a UI/Pages.

import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";
import { TABLES } from "./eventsService.js";

// =========================
// Helpers internos
// =========================
function assertId(id, name = "id") {
  if (id === null || id === undefined || String(id).trim() === "") {
    throw new Error(`${name} es obligatorio.`);
  }
}

function pickColumns(obj, whitelist) {
  if (!whitelist) return { ...obj };
  const out = {};
  for (const k of whitelist) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  }
  return out;
}

function throwSupabase(error, context = "") {
  if (!error) return;
  const n = normalizeSupabaseError(error);
  const msg = context ? `${context}: ${n.message}` : n.message;
  const e = new Error(msg);
  e.code = n.code;
  e.details = n.details;
  e.hint = n.hint;
  e.raw = n.raw;
  throw e;
}

// =========================
// CONTACTS: lecturas
// =========================

/**
 * Lista contactos por team_id.
 */
export async function listContactsByTeam(
  teamId,
  { orderBy = "name", ascending = true, select = "*" } = {}
) {
  assertId(teamId, "teamId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.CONTACTS)
    .select(select)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando contacts");
  return data || [];
}

/**
 * Obtiene contacto por id.
 */
export async function getContactById(contactId, { select = "*" } = {}) {
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.CONTACTS)
    .select(select)
    .eq("id", contactId)
    .single();

  throwSupabase(error, "Error leyendo contact");
  return data;
}

/**
 * Búsqueda simple (ILIKE) por nombre/email/teléfono.
 * Ojo: Supabase PostgREST no permite OR complejo sin `or()` string.
 */
export async function searchContacts(teamId, query, { limit = 25, select = "*" } = {}) {
  assertId(teamId, "teamId");
  const q = String(query || "").trim();
  if (!q) return [];

  const supabase = getSupabaseClient();

  // OR: name ilike, email ilike, phone ilike
  const or = [
    `name.ilike.%${q}%`,
    `email.ilike.%${q}%`,
    `phone.ilike.%${q}%`,
  ].join(",");

  const { data, error } = await supabase
    .from(TABLES.CONTACTS)
    .select(select)
    .eq("team_id", teamId)
    .or(or)
    .limit(limit);

  throwSupabase(error, "Error buscando contacts");
  return data || [];
}

// =========================
// CONTACTS: escrituras
// =========================

/**
 * Crea contacto.
 * options:
 * - whitelist: string[] (si quieres)
 * - select: string
 */
export async function createContact(payload, { whitelist = null, select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  const supabase = getSupabaseClient();

  const insertPayload = pickColumns(payload, whitelist);

  const { data, error } = await supabase
    .from(TABLES.CONTACTS)
    .insert(insertPayload)
    .select(select)
    .single();

  throwSupabase(error, "Error creando contact");
  return data;
}

/**
 * Actualiza contacto.
 */
export async function updateContact(contactId, patch, { whitelist = null } = {}) {
  assertId(contactId, "contactId");
  if (!patch || typeof patch !== "object") throw new Error("patch inválido.");
  const supabase = getSupabaseClient();

  const updatePayload = pickColumns(patch, whitelist);

  const { error } = await supabase.from(TABLES.CONTACTS).update(updatePayload).eq("id", contactId);
  throwSupabase(error, "Error actualizando contact");
  return true;
}

/**
 * Borra contacto.
 */
export async function deleteContact(contactId) {
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.CONTACTS).delete().eq("id", contactId);
  throwSupabase(error, "Error borrando contact");
  return true;
}

// =========================
// EVENT_CONTACTS (linkado)
// =========================

/**
 * Crea vínculo event_contact.
 * payload típico:
 * { event_id, contact_id, is_primary, role_in_event, notes }
 */
export async function linkContactToEvent(payload, { select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  if (!payload.event_id) throw new Error("payload.event_id obligatorio.");
  if (!payload.contact_id) throw new Error("payload.contact_id obligatorio.");

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .insert(payload)
    .select(select)
    .single();

  throwSupabase(error, "Error linkando contact a event");
  return data;
}

/**
 * Borra vínculo event_contact por id (fila link).
 */
export async function unlinkEventContact(linkId) {
  assertId(linkId, "linkId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.EVENT_CONTACTS).delete().eq("id", linkId);
  throwSupabase(error, "Error borrando vínculo event_contact");
  return true;
}

/**
 * Borra vínculo por event_id + contact_id (si prefieres).
 */
export async function unlinkContactFromEvent(eventId, contactId) {
  assertId(eventId, "eventId");
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .delete()
    .eq("event_id", eventId)
    .eq("contact_id", contactId);

  throwSupabase(error, "Error borrando vínculo contact->event");
  return true;
}

/**
 * Devuelve todos los contactos de un evento con los campos que pide el
 * contrato (DNI y domicilio fiscal), el primary delante.
 *
 * listEventContacts() de eventsService no sirve aqui: su select se queda en
 * nombre, email y telefono, que es justo lo que al contrato no le basta.
 */
export async function listContactsForEvent(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .select(
      "id, contact_id, is_primary, role_in_event, "
      + "contacts:contact_id (id, type, name, surname, email, phone, nif, fiscal_address, fiscal_city, fiscal_zip)"
    )
    .eq("event_id", eventId)
    .order("is_primary", { ascending: false })
    .order("created_at", { ascending: true });

  throwSupabase(error, "Error cargando contactos del evento");

  return (data || [])
    .filter((row) => row.contacts)
    .map((row) => ({
      ...row.contacts,
      is_primary: row.is_primary,
      role_in_event: row.role_in_event,
    }));
}

/**
 * Devuelve el contacto primary de un evento (con join a contacts) o null.
 */
export async function getPrimaryContactForEvent(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .select("id, contact_id, is_primary, role_in_event, contacts:contact_id (id, type, name, surname, email, phone, nif, fiscal_address, fiscal_city, fiscal_zip)")
    .eq("event_id", eventId)
    .eq("is_primary", true)
    .maybeSingle();

  throwSupabase(error, "Error leyendo contacto primary del evento");
  if (!data) return null;

  // shape amigable
  return {
    link: {
      id: data.id,
      contact_id: data.contact_id,
      is_primary: data.is_primary,
      role_in_event: data.role_in_event,
    },
    contact: data.contacts || null,
  };
}

/**
 * Establece un contacto como primary para un evento.
 * (Delegamos en eventsService para mantener lógica en un solo sitio.)
 */
export async function setPrimaryContactForEvent(eventId, contactId, options = {}) {
  // Import dinámico para evitar ciclos de import (contactsService <-> eventsService)
  const mod = await import("./eventsService.js");
  return mod.upsertPrimaryEventContact(eventId, contactId, options);
}
